import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { FiArrowLeft, FiArrowRight, FiCheck, FiTrendingUp, FiUsers, FiGlobe, FiStar, FiShield, FiMail, FiPhone, FiDownload } from 'react-icons/fi';

const C = {
  rose: '#e3425c', roseLight: 'rgba(227,66,92,0.08)', roseDeep: '#c94d5c',
  gold: '#d4a853', goldLight: 'rgba(212,168,83,0.12)', goldDark: '#b8923e',
  dark: '#1c1c1e', darkLight: '#2a2a2e', cream: '#f7f7f8', creamDark: '#ebebed',
  white: '#ffffff', gray: '#8e8e93', grayLight: '#aeaeb2',
};

export default function Partnerships() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', type: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.type) { setError('Please fill in all required fields'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/partnerships', form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    }
    setLoading(false);
  };

  const inquiryTypes = [
    { value: 'investor', label: 'Investment Opportunity', desc: 'I want to invest in Grand Gather Registry' },
    { value: 'partner', label: 'Strategic Partnership', desc: 'I want to partner with GGR for mutual growth' },
    { value: 'retail', label: 'Retail / Store Partnership', desc: 'I want to list my store or products on GGR' },
    { value: 'sponsor', label: 'Sponsorship', desc: 'I want to sponsor events or advertise on the platform' },
    { value: 'media', label: 'Media / Press', desc: 'Media inquiry, interview, or press coverage' },
    { value: 'other', label: 'Other', desc: 'General business inquiry' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: C.dark }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .part-input { width: 100%; padding: 12px 16px; border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; background: rgba(255,255,255,0.04); color: white; font-size: 14px; font-family: 'Inter', sans-serif; outline: none; transition: border-color 0.2s; }
        .part-input:focus { border-color: ${C.rose}; box-shadow: 0 0 0 3px ${C.roseLight}; }
        .part-input::placeholder { color: rgba(255,255,255,0.25); }
        .type-card { padding: 16px 20px; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; cursor: pointer; transition: all 0.2s; background: rgba(255,255,255,0.02); }
        .type-card:hover { border-color: ${C.rose}50; background: rgba(227,66,92,0.04); }
        .type-card.selected { border-color: ${C.rose}; background: rgba(227,66,92,0.08); }
        @media (max-width: 768px) { .part-stats { grid-template-columns: 1fr 1fr !important; } }
      `}</style>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.creamDark}`, padding: '14px 0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to='/' style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: C.dark, fontSize: '14px', fontWeight: 500 }}>
            <FiArrowLeft size={16} />
            <img src="/ggr-logo-deck.png" alt='GGR' style={{ height: '50px', width: 'auto' }} />
          </Link>
          <Link to='/register' style={{ padding: '10px 22px', borderRadius: '8px', background: C.rose, color: C.white, textDecoration: 'none', fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
            Get Started
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '60px 28px 40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ display: 'inline-block', padding: '6px 18px', borderRadius: '20px', background: `${C.rose}18`, color: C.rose, fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '20px' }}>
          Investors & Partners
        </div>
        <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 700, color: C.white, lineHeight: 1.1, marginBottom: '16px' }}>
          Grow with <span style={{ color: C.rose }}>Grand Gather</span>
        </h1>
        <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.45)', maxWidth: '560px', margin: '0 auto', lineHeight: 1.7 }}>
          We're building South Africa's leading event planning and gift registry platform. Join us as an investor, strategic partner, or retail collaborator.
        </p>

        {/* Investor Deck Download */}
        <div style={{ marginTop: '28px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href='#inquiry' style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', background: C.rose, color: C.white, borderRadius: '10px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, boxShadow: `0 4px 16px ${C.rose}40` }}>
            Get in Touch <FiArrowRight size={16} />
          </a>
          <a href='mailto:info@grandgatherregistry.co.za?subject=Investor Deck Request' style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', background: 'transparent', color: C.gold, border: `1px solid ${C.gold}40`, borderRadius: '10px', textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>
            <FiDownload size={16} /> Investor Deck
          </a>
        </div>
      </section>

      {/* Stats */}
      <section style={{ maxWidth: '900px', margin: '0 auto', padding: '0 28px 50px' }}>
        <div className='part-stats' style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', background: 'rgba(255,255,255,0.04)', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
          {[
            { value: 'R50B+', label: 'SA Events Market', color: C.rose },
            { value: '11', label: 'Provinces Covered', color: C.gold },
            { value: '5+', label: 'Revenue Streams', color: C.rose },
            { value: 'AI', label: 'Powered by GiGi', color: C.gold },
          ].map((s, i) => (
            <div key={i} style={{ background: C.dark, padding: '28px 16px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '36px', color: s.color, marginBottom: '4px', fontWeight: 700 }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Partner */}
      <section style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 28px 60px' }}>
        <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(26px, 3vw, 34px)', color: C.white, textAlign: 'center', marginBottom: '36px' }}>
          Why Partner <span style={{ color: C.rose }}>With Us</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {[
            { icon: <FiTrendingUp size={22} />, title: 'High Growth Market', desc: 'South Africa hosts millions of events annually — weddings, birthdays, corporate, traditional ceremonies. The market is massive and largely untapped digitally.', color: C.rose },
            { icon: <FiUsers size={22} />, title: 'Multi-Sided Platform', desc: 'We connect hosts, vendors, gift givers, and sponsors — creating a network effect that grows stronger with each user.', color: C.gold },
            { icon: <FiGlobe size={22} />, title: 'Scalable Across Africa', desc: 'Built for South Africa first, designed to expand across the continent. Events are universal — our platform scales naturally.', color: C.rose },
            { icon: <FiStar size={22} />, title: 'AI-Powered Planning', desc: 'Our AI assistant GiGi helps users plan events intelligently — multi-language support, vendor matching, budget optimization.', color: C.gold },
            { icon: <FiShield size={22} />, title: 'Vetted Vendors', desc: 'Every vendor is verified with full address, social media, and business documentation — building trust in the marketplace.', color: C.rose },
            { icon: <FiTrendingUp size={22} />, title: '5+ Revenue Streams', desc: 'Commissions, subscriptions, gift codes, sponsored slots, vendor plans — diversified and sustainable revenue model.', color: C.gold },
          ].map((item, i) => (
            <div key={i} style={{ padding: '28px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ color: item.color, marginBottom: '14px' }}>{item.icon}</div>
              <h4 style={{ color: C.white, fontSize: '15px', marginBottom: '8px', fontWeight: 600 }}>{item.title}</h4>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', lineHeight: 1.7 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Partnership Tiers */}
      <section style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 28px 60px' }}>
        <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(26px, 3vw, 34px)', color: C.white, textAlign: 'center', marginBottom: '36px' }}>
          Partnership <span style={{ color: C.gold }}>Tiers</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
          {[
            { tier: 'Silver', price: 'Free', color: C.gray, features: ['Basic listing', 'Event support', 'Community access'] },
            { tier: 'Gold', price: 'R299/mo', color: C.gold, features: ['Featured listing', 'Analytics dashboard', 'Priority support', 'Gift registry integration'] },
            { tier: 'Platinum', price: 'R799/mo', color: C.rose, features: ['Top placement', 'Full analytics', 'Dedicated account manager', 'Co-marketing opportunities', 'API access'] },
          ].map((t, i) => (
            <div key={i} style={{ padding: '32px 24px', borderRadius: '16px', border: `1px solid ${t.color}30`, background: `${t.color}05`, textAlign: 'center' }}>
              <div style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '22px', fontWeight: 700, color: t.color, marginBottom: '4px' }}>{t.tier}</div>
              <div style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '28px', color: C.white, marginBottom: '20px' }}>{t.price}</div>
              {t.features.map((f, j) => (
                <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', padding: '6px 0', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                  <FiCheck size={14} style={{ color: t.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>{f}</span>
                </div>
              ))}
              <button onClick={() => document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' })}
                style={{ marginTop: '20px', width: '100%', padding: '12px', borderRadius: '8px', border: `1px solid ${t.color}60`, background: 'transparent', color: t.color, fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}>
                Get Started
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Inquiry Form */}
      <section id='inquiry' style={{ maxWidth: '680px', margin: '0 auto', padding: '0 28px 80px' }}>
        <div style={{ padding: '40px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `${C.gold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <FiCheck size={28} style={{ color: C.gold }} />
              </div>
              <h3 style={{ fontFamily: 'Playfair Display, Georgia, serif', color: C.white, fontSize: '24px', marginBottom: '8px' }}>Thank You!</h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.7, maxWidth: '400px', margin: '0 auto' }}>
                Your inquiry has been received. Our team will review it and get back to you within 2-3 business days.
              </p>
              <Link to='/' style={{ display: 'inline-block', marginTop: '24px', padding: '12px 28px', background: C.rose, color: C.white, borderRadius: '10px', textDecoration: 'none', fontWeight: 600, fontSize: '14px' }}>
                Back to Home
              </Link>
            </div>
          ) : (
            <>
              <h3 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '24px', color: C.white, marginBottom: '6px', textAlign: 'center' }}>Get in Touch</h3>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', textAlign: 'center', marginBottom: '28px' }}>Tell us about your interest and we'll take it from there.</p>

              {error && <div style={{ padding: '12px', background: `${C.rose}15`, borderRadius: '8px', color: C.rose, marginBottom: '16px', fontSize: '13px' }}>{error}</div>}

              <label style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px', display: 'block' }}>What type of inquiry is this? *</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '24px' }}>
                {inquiryTypes.map(t => (
                  <div key={t.value} className={`type-card${form.type === t.value ? ' selected' : ''}`} onClick={() => setForm({ ...form, type: t.value })}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: form.type === t.value ? C.rose : 'rgba(255,255,255,0.7)', marginBottom: '2px' }}>{t.label}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{t.desc}</div>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px', display: 'block' }}>Full Name *</label>
                    <input className='part-input' value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder='Your full name' required />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px', display: 'block' }}>Email *</label>
                    <input className='part-input' type='email' value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder='your@email.com' required />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px', display: 'block' }}>Phone</label>
                    <input className='part-input' value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder='+27 ...' />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px', display: 'block' }}>Company</label>
                    <input className='part-input' value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder='Your company name' />
                  </div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px', display: 'block' }}>Message</label>
                  <textarea className='part-input' value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4} placeholder="Tell us about your interest and how you'd like to collaborate..." style={{ resize: 'vertical' }} />
                </div>
                <button type='submit' disabled={loading} style={{ width: '100%', padding: '14px', background: `linear-gradient(135deg, ${C.rose}, ${C.roseDeep})`, color: C.white, border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, transition: 'all 0.2s', fontFamily: 'Inter, sans-serif', boxShadow: `0 4px 16px ${C.rose}40` }}>
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '32px', padding: '24px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px', marginBottom: '12px' }}>Prefer to reach out directly?</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <a href='mailto:info@grandgatherregistry.co.za' style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.6)', fontSize: '13px', textDecoration: 'none' }}>
              <FiMail size={14} style={{ color: C.rose }} /> info@grandgatherregistry.co.za
            </a>
            <a href='https://wa.me/27613911942' target='_blank' rel='noopener noreferrer' style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.6)', fontSize: '13px', textDecoration: 'none' }}>
              <FiPhone size={14} style={{ color: C.rose }} /> +27 61 391 1942
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 28px', textAlign: 'center' }}>
        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.2)' }}>
          &copy; 2025–2026 Grand Gather Registry (Pty) Ltd. All rights reserved. Made with <span style={{ color: C.rose }}>&hearts;</span> in South Africa
        </p>
      </footer>
    </div>
  );
}